export function convertSvgPathToRelative(
    absolutePath: string,
    viewBoxWidth: number,
    viewBoxHeight: number
): string {
    // Проверяем что absolutePath это строка
    if (typeof absolutePath !== 'string') {
        console.error('convertSvgPathToRelative: absolutePath is not a string:', absolutePath);
        return '';
    }
    
    const commands = absolutePath.match(/[MLHVCSQTAZmlhvcsqtaz][^MLHVCSQTAZmlhvcsqtaz]*/g);
    if (!commands) return absolutePath;

    return commands.map(cmd => {
        const command = cmd[0].toUpperCase();
        const values = cmd.slice(1).trim().split(/[\s,]+/).map(Number);

        if (!values.length || values.some(isNaN)) return cmd[0];

        let convertedValues: string[];

        // Handle different command types
        if (command === 'H') {
            // Horizontal line - only X coordinates
            convertedValues = values.map(value => {
                const result = value / viewBoxWidth;
                if (result === 0) return '0';
                if (result === 1) return '1';
                return (Math.round(result * 10000) / 10000).toString();
            });
        } else if (command === 'V') {
            // Vertical line - only Y coordinates
            convertedValues = values.map(value => {
                const result = value / viewBoxHeight;
                if (result === 0) return '0';
                if (result === 1) return '1';
                return (Math.round(result * 10000) / 10000).toString();
            });
        } else if (command === 'A') {
            // Arc command: rx ry x-axis-rotation large-arc-flag sweep-flag x y
            convertedValues = values.map((value, index) => {
                if (index === 0) {
                    // rx
                    const result = value / viewBoxWidth;
                    return (Math.round(result * 10000) / 10000).toString();
                } else if (index === 1) {
                    // ry
                    const result = value / viewBoxHeight;
                    return (Math.round(result * 10000) / 10000).toString();
                } else if (index === 2 || index === 3 || index === 4) {
                    // rotation, large-arc-flag, sweep-flag - keep as is
                    return value.toString();
                } else if (index === 5) {
                    // x
                    const result = value / viewBoxWidth;
                    return (Math.round(result * 10000) / 10000).toString();
                } else {
                    // y
                    const result = value / viewBoxHeight;
                    return (Math.round(result * 10000) / 10000).toString();
                }
            });
        } else {
            // Default handling for M, L, C, S, Q, T, Z
            convertedValues = values.map((value, index) => {
                const isXCoordinate = index % 2 === 0;
                const divisor = isXCoordinate ? viewBoxWidth : viewBoxHeight;
                const result = value / divisor;

                if (result === 0) return '0';
                if (result === 1) return '1';

                return (Math.round(result * 10000) / 10000).toString();
            });
        }

        return cmd[0] + convertedValues.join(' ');
    }).join('');
}

import React from 'react';

export function createRelativeClipPath(
    absolutePath: string,
    viewBoxWidth: number,
    viewBoxHeight: number,
    clipPathId: string
): React.ReactElement {
    const relativePath = convertSvgPathToRelative(absolutePath, viewBoxWidth, viewBoxHeight);

    return (
        <svg style={{position: 'absolute', width: 0, height: 0}}>
            <defs>
                <clipPath id={clipPathId} clipPathUnits="objectBoundingBox">
                    <path d={relativePath}/>
                </clipPath>
            </defs>
        </svg>
    );
}

export function createMultipleRelativeClipPath(
    paths: string[],
    viewBoxWidth: number,
    viewBoxHeight: number,
    clipPathId: string
): React.ReactElement {
    return (
        <svg style={{position: 'absolute', width: 0, height: 0}}>
            <defs>
                <clipPath id={clipPathId} clipPathUnits="objectBoundingBox">
                    {paths.map((path, index) => {
                        if (typeof path !== 'string') {
                            console.error(`Path at index ${index} is not a string:`, path);
                            return null;
                        }
                        const relativePath = convertSvgPathToRelative(path, viewBoxWidth, viewBoxHeight);
                        return <path key={index} d={relativePath}/>;
                    })}
                </clipPath>
            </defs>
        </svg>
    );
}

export function createVisibleSvgBorder(
    absolutePath: string,
    viewBoxWidth: number,
    viewBoxHeight: number,
    strokeWidth: number = 2,
    color: string = "currentColor"
): React.ReactElement {
    return (
        <svg
            width={viewBoxWidth}
            height={viewBoxHeight}
            viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
        >
            <path
                d={absolutePath}
                fill="none"
                stroke={color}
                strokeWidth={strokeWidth}
            />
        </svg>
    );
}

export function createGradientBorderSvg(
    absolutePath: string,
    viewBoxWidth: number,
    viewBoxHeight: number,
    strokeWidth: number = 4,
    useGradient: boolean = true,
    solidColor: string = "#5282FF"
): React.ReactElement {
    const gradientColors = [
        {offset: "0", color: "#5282FF"},
        {offset: "0.25", color: "#A9BBEA"},
        {offset: "0.5", color: "#0C47DF"},
        {offset: "0.75", color: "#2455D6"},
        {offset: "1", color: "#1C409C"}
    ];
    const gradientId = "borderGradient";

    return (
        <svg
            width={viewBoxWidth}
            height={viewBoxHeight}
            viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
            fill="none"
        >
            {useGradient && (
                <defs>
                    <linearGradient
                        id={gradientId}
                        x1={viewBoxWidth / 2}
                        y1="0"
                        x2={viewBoxWidth / 2}
                        y2={viewBoxHeight * 1.24}
                        gradientUnits="userSpaceOnUse"
                    >
                        {gradientColors.map((stop, index) => (
                            <stop
                                key={index}
                                offset={stop.offset}
                                stopColor={stop.color}
                            />
                        ))}
                    </linearGradient>
                </defs>
            )}
            <path
                d={absolutePath}
                fill="none"
                stroke={useGradient ? `url(#${gradientId})` : solidColor}
                strokeWidth={strokeWidth}
            />
        </svg>
    );
}

export function createFilteredSvg(
    absolutePath: string,
    viewBoxWidth: number,
    viewBoxHeight: number,
    fillColor: string = "#001F61",
    fillOpacity: number = 0.5,
    filterId: string = "innerShadowFilter",
    showFill: boolean = true
): React.ReactElement {
    const filterWidth = viewBoxWidth;
    const filterHeight = viewBoxHeight + 30.866; // Увеличиваем для теней
    const filterY = -25.866; // Смещение для верхней тени

    return (
        <svg
            width={viewBoxWidth}
            height={viewBoxHeight}
            viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
            fill="none"
        >
            <defs>
                <filter
                    id={filterId}
                    x="0"
                    y={filterY}
                    width={filterWidth}
                    height={filterHeight}
                    filterUnits="userSpaceOnUse"
                    colorInterpolationFilters="sRGB"
                >
                    <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                    <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>

                    {/* Первая внутренняя тень */}
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                   result="hardAlpha"/>
                    <feMorphology radius="8.33114" operator="dilate" in="SourceAlpha" result="effect1_innerShadow"/>
                    <feOffset dy="-23.8033"/>
                    <feGaussianBlur stdDeviation="9.99737"/>
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                    <feColorMatrix type="matrix"
                                   values="0 0 0 0 0.0887131 0 0 0 0 0.256164 0 0 0 0 0.692233 0 0 0 1 0"/>
                    <feBlend mode="normal" in2="shape" result="effect1_innerShadow"/>

                    {/* Вторая внутренняя тень */}
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                   result="hardAlpha"/>
                    <feMorphology radius="32.1344" operator="dilate" in="SourceAlpha" result="effect2_innerShadow"/>
                    <feOffset dy="-26"/>
                    <feGaussianBlur stdDeviation="29"/>
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 0.321569 0 0 0 0 0.509804 0 0 0 0 1 0 0 0 1 0"/>
                    <feBlend mode="color-dodge" in2="effect1_innerShadow" result="effect2_innerShadow"/>

                    {/* Третья внутренняя тень */}
                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                                   result="hardAlpha"/>
                    <feMorphology radius="4.76065" operator="dilate" in="SourceAlpha" result="effect3_innerShadow"/>
                    <feOffset dy="5"/>
                    <feGaussianBlur stdDeviation="25"/>
                    <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
                    <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 1 0"/>
                    <feBlend mode="plus-lighter" in2="effect2_innerShadow" result="effect3_innerShadow"/>
                </filter>
            </defs>
            <g filter={`url(#${filterId})`}>
                <path
                    d={absolutePath}
                    fill={showFill ? fillColor : "none"}
                    fillOpacity={showFill ? fillOpacity : 0}
                />
            </g>
        </svg>
    );
}
